import SpeechBubble from '@design-system/components/molecules/SpeechBubble'
import { Icon } from '@design-system/index'
import styled from '@emotion/styled'
import { colors, typo, zIndex } from '@mathflat/common/style'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import clsx from 'clsx'
import { addDays, isAfter } from 'date-fns'
import { observer } from 'mobx-react'
import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router'

import { Badge } from '@/component/atoms/Badge'
import { CustomEventName, getContractUrl } from '@/constant'
import type { Banner } from '@/dto'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'
import { getMyPageNavigation } from '@/router/constant'
import BannerService from '@/service/banner.service'
import modalService from '@/service/modal.service'
import { localStorageService } from '@/service/storage.service'
import WebviewService from '@/service/webview.service'
import { openLink } from '@/utils/common'
import {
  connectToScienceflat,
  getScienceflatBaseUrl,
  openScienceflat,
} from '@/utils/scienceflatUtils'
import { _mixpanelUtils } from '@/utils/trackings/_mixpanel'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import { Portal } from '../Portal'
import TooltipBanner from './TooltipBanner'

const Profile = () => {
  const isMyInfo = useRouteMatch(RouteName.myInfo)
  const { pathname } = useLocation()
  const [isBannerClosed, setIsBannerClosed] = useState(false)
  const isBannerOn = useMemo(() => {
    // 닫은 적 없거나, 닫은 지 24시간 지났으면 노출 가능
    const closedAt = localStorageService.getItem('CONTRACT_TOOLTIP_CLOSED_AT')
    const canShowByTime = !closedAt || isAfter(new Date(), addDays(new Date(closedAt), 1))
    if (commonRepo.isSchoolflat) {
      return !!(
        commonRepo.academy?.customerType !== 'B2B-제휴-김과외' &&
        commonRepo.academy?.status === 'FREE_INPROGRESS' &&
        commonRepo.user?.isAdmin &&
        !isMyInfo &&
        !isBannerClosed &&
        canShowByTime
      )
    }

    return !!(
      commonRepo.academy?.customerType !== 'B2B-제휴-김과외' &&
      commonRepo.academy?.status === 'FREE_INPROGRESS' &&
      !commonRepo.academy.isFreeInProgressSales &&
      commonRepo.user?.isAdmin &&
      !isMyInfo &&
      !isBannerClosed &&
      canShowByTime
    )
  }, [isMyInfo, pathname, commonRepo.academy, commonRepo.user, isBannerClosed])

  useEffect(() => {
    const onExpired = () => {
      setIsBannerClosed(false)
    }

    window.addEventListener(CustomEventName.Banner.onExpired, onExpired)

    return () => {
      window.removeEventListener(CustomEventName.Banner.onExpired, onExpired)
    }
  }, [])

  if (commonRepo.isSchoolflat) {
    return (
      <SchoolflatProfile
        banner={BannerService.banner}
        isBannerOn={isBannerOn}
        setIsBannerClosed={setIsBannerClosed}
      />
    )
  }

  return (
    <MathflatProfile
      isBannerOn={isBannerOn}
      banner={BannerService.banner}
      setIsBannerClosed={setIsBannerClosed}
    />
  )
}

const MathflatProfile = ({
  banner,
  isBannerOn,
  setIsBannerClosed,
}: {
  banner?: Banner
  isBannerOn: boolean
  setIsBannerClosed: Dispatch<SetStateAction<boolean>>
}) => {
  const onCloseButtonClick = () => {
    setIsBannerClosed(true)
    localStorageService.setItem('CONTRACT_TOOLTIP_CLOSED_AT')(new Date().getTime())
    _mixpanelUtils.uiTrack('정식가입하러가기툴팁_닫기')
  }

  const onTooltipBannerClick = (banner: Banner) => {
    if (banner) {
      let url = banner.redirectUrl

      if (url && !url.startsWith('http')) {
        url = 'http://' + url
      }

      if (url && url.includes(`contract.${domainProvider.TARGET_DOMAIN}`) && commonRepo.user) {
        url = getContractUrl(commonRepo.user, url)
      }

      if (url) {
        openLink(url)
      }

      _mixpanelUtils.uiTrack('정식가입하러가기툴팁_신청하기클릭')
    }
  }

  if (!banner) return <_Profile />

  return (
    <SpeechBubble
      visible={isBannerOn}
      closeButtonOpt={{ hasCloseButton: true, isCloseButtonVisible: false, style: { top: 12 } }}
      showArrow={false}
      Overlay={<TooltipBanner banner={banner} onClick={onTooltipBannerClick} />}
      overlayBackgroundColor="transparent"
      overlayBorderColor="transparent"
      placement="bottom-right"
      align={{ offset: [-23, -2] }}
      overlayStyle={{ width: 280 }}
      onCloseButtonClick={onCloseButtonClick}
    >
      <_Profile />
    </SpeechBubble>
  )
}

const SchoolflatProfile = ({
  banner,
  isBannerOn,
  setIsBannerClosed,
}: {
  banner?: Banner
  isBannerOn: boolean
  setIsBannerClosed: Dispatch<SetStateAction<boolean>>
}) => {
  const showSchoolflatTeacherGuideTooltip = useMemo(() => {
    if (!commonRepo.isSchoolflat) return false
    const closedAt = localStorageService.getItem('SCHOOLFLAT_TEACHER_GUIDE_TOOLTIP_CLOSED_AT')
    if (!closedAt) return true
    const oneDayAfterClosedAt = addDays(new Date(closedAt), 1)
    return isAfter(new Date(), oneDayAfterClosedAt)
  }, [commonRepo.isSchoolflat])

  if (banner && isBannerOn) {
    return (
      <MathflatProfile
        banner={banner}
        isBannerOn={isBannerOn}
        setIsBannerClosed={setIsBannerClosed}
      />
    )
  }

  return <_Profile />
}

const _Profile = observer(() => {
  const ref = useRef<HTMLButtonElement>(null)
  const rect = ref.current?.getBoundingClientRect()
  const history = useHistory()
  const { path } = useRouteMatch()

  const [isNavigationOpened, setIsNavigationOpened] = useState(false)

  useEffect(() => {
    const handleNavigationOpened = () => {
      setIsNavigationOpened(false)
    }
    document.addEventListener('click', handleNavigationOpened)

    return () => {
      document.removeEventListener('click', handleNavigationOpened)
    }
  }, [])

  const handleScienceflatConnect = async () => {
    if (!commonRepo.user || !commonRepo.academy) return

    try {
      if (commonRepo.user.isScienceflatAllowed && commonRepo.academy.isScienceflatAllowed) {
        await openScienceflat()
      } else {
        if (commonRepo.user.isAdmin) {
          modalService.openConfirmModal({
            content: (
              <ScienceflatConsentContent>
                <p className="title">사이언스플랫 이용 동의</p>
                <p>
                  매쓰플랫 선생님은 <strong>사이언스플랫을 무료로 이용</strong>하실 수 있어요!
                  <br />
                  사이언스플랫은 새로운 서비스라 처음 이용하실 때 한번의 동의가 필요해요.
                  <br />
                  동의하고 바로 사이언스플랫을 사용해보세요.
                </p>
              </ScienceflatConsentContent>
            ),
            actionButtonText: '동의',
            cancelButtonText: '취소',
            onClick: async () => {
              await connectToScienceflat(
                `${getScienceflatBaseUrl()}/mathflat/callback`,
                !WebviewService.isAppAndroid,
              )
              modalService.closeModal()
            },
          })
        } else {
          modalService.openConfirmModal({
            content: (
              <ScienceflatNoPermissionContent>
                <p className="body-text">
                  선생님, 사이언스플랫 이용 권한이 없습니다.
                  <br />
                  관리자 선생님에게 이용 권한을 요청해주세요!
                </p>
                <p className="sub-text">
                  (관리자 선생님이 사이언스플랫 &apos;운영 관리&apos; &gt; &apos;선생님
                  관리&apos;에서 권한을 설정할 수 있습니다.)
                </p>
              </ScienceflatNoPermissionContent>
            ),
            actionButtonText: '확인',
            hasCancelButton: false,
            onClick: modalService.closeModal,
          })
        }
      }
    } catch {
      modalService.openConfirmModal({
        content: '사이언스플랫 연동 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        actionButtonText: '확인',
        hasCancelButton: false,
        onClick: modalService.closeModal,
      })
    }
  }

  const navInfoList = useMemo(
    () =>
      getMyPageNavigation({
        permission: commonRepo.permission.get,
        actions: { scienceflatConnect: handleScienceflatConnect },
        featurePermission: commonRepo.permission.byFeature,
      }),
    [commonRepo.permission.get],
  )

  const showScienceflatBadge = commonRepo.permission.byFeature.isAllowed('CONTENT_SCIENCEFLAT')

  const ProfileIcon = (() => {
    if (commonRepo.permission.hasPremiumAuth) {
      return (
        <div className="profile__star-wrapper">
          <Icon className="profile__star-icon" name="star_fill" size={10} color="white" />
        </div>
      )
    }
    return (
      <Icon className="profile__profile-icon" name="profile" size={20} color={colors.gray.$400} />
    )
  })()

  return (
    <button
      className={clsx(
        'header__profile',
        path.includes(RouteName.myInfo) && 'active',
        isNavigationOpened && 'opened',
      )}
      ref={ref}
      onClick={(e) => {
        e.stopPropagation()
        setIsNavigationOpened(!isNavigationOpened)
        mixpanelClient.track('GNB_마이페이지버튼_Click', {
          펼침여부: isNavigationOpened,
        })
      }}
    >
      <Portal.Element
        isVisible={isNavigationOpened}
        rect={rect}
        offset={{ x: rect?.width ?? 0, y: 0 }}
      >
        <ProfilePopover>
          {navInfoList.map((navInfo, index) => {
            if (!navInfo) return null
            return (
              <React.Fragment key={index}>
                {navInfo.title && <div className="profile__popover-title">{navInfo.title}</div>}
                <div className="profile__popover-group">
                  {navInfo.items.map((item, index) => {
                    if (!item) return null
                    return (
                      <button
                        key={item.label ?? index}
                        className="profile__popover-item"
                        disabled={item.disabled}
                        onClick={() => {
                          if (item.action) {
                            item.action()
                            return
                          }

                          if (item.to) history.push(item.to)
                        }}
                      >
                        {item.labelContent ?? item.label}
                        {item.isNew && (
                          <div className="new-badge">
                            <img
                              src={s3URL.mathflatAssetsBucket.graphic('graphic-new.svg')}
                              alt="new"
                              width={14}
                              height={14}
                            />
                          </div>
                        )}
                      </button>
                    )
                  })}
                </div>
              </React.Fragment>
            )
          })}
        </ProfilePopover>
      </Portal.Element>
      {showScienceflatBadge && <ScienceflatGradientBg />}
      {ProfileIcon}
      <span className="profile__teacher-nickname">{commonRepo.user?.name}</span>
      {showScienceflatBadge && (
        <ScienceflatBadgeWrapper>
          <Badge
            size="xs"
            backgroundColor="var(--Colors-Green-500)"
            color="var(--White)"
            radius="100"
          >
            과학 OPEN!
          </Badge>
          <ScienceflatSparkle aria-hidden>✦</ScienceflatSparkle>
        </ScienceflatBadgeWrapper>
      )}
      <div className="profile__chevron-wrapper">
        <Icon name="chevron_down" size={20} color="var(--Colors-Gray-700)" />
      </div>
    </button>
  )
})
export default observer(Profile)

const ProfilePopover = styled.nav`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 220px;
  top: 41px;
  right: 0;
  border-radius: var(--Radius-200);
  border: 1px solid var(--Colors-Gray-300);
  background: var(--White);
  box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: ${zIndex.PopperNavigation};
  overflow: hidden;

  .profile__popover-title {
    ${typo.caption01};
    font-weight: 400;
    color: var(--Colors-Gray-600);
    padding: 14px 16px;
  }

  .profile__popover-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-bottom: 1px solid var(--Colors-Gray-300);
  }

  .profile__popover-item {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 14px 16px;
    ${typo.body02};
    color: var(--Colors-Gray-900);
    font-weight: 400;
    text-align: left;

    @media (hover: hover) {
      &:hover {
        background-color: var(--Colors-Gray-100);
      }
    }

    &:active {
      background-color: var(--Colors-Gray-300);
    }

    &:disabled {
      cursor: not-allowed;

      &:hover,
      &:active {
        background-color: transparent;
      }
    }

    .new-badge {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 14px;
      height: 14px;
      border-radius: var(--Radius-100);
      background: var(--Colors-Orange-500);
    }
  }

  .profile__popover-divider {
    height: 1px;
    background-color: ${colors.gray.$300};
  }
`

const ScienceflatGradientBg = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    110deg,
    transparent 30%,
    rgba(34, 197, 94, 0.22) 50%,
    transparent 70%
  );
  transform: translateX(-110%);
  pointer-events: none;

  @keyframes chip-shimmer {
    0% {
      transform: translateX(-110%);
    }
    55% {
      transform: translateX(110%);
    }
    100% {
      transform: translateX(110%);
    }
  }

  animation: chip-shimmer 2.8s ease-in-out infinite;
`

const ScienceflatBadgeWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  border-radius: var(--Radius-100);

  @keyframes pip-sync-glow {
    0%,
    38%,
    90%,
    100% {
      box-shadow:
        0 0 0 0 rgba(34, 197, 94, 0),
        0 0 0 0 rgba(34, 197, 94, 0);
      filter: brightness(1);
    }
    58% {
      box-shadow:
        0 0 0 2px rgba(34, 197, 94, 0.3),
        0 0 14px 4px rgba(34, 197, 94, 0.2),
        0 0 28px 10px rgba(34, 197, 94, 0.1);
      filter: brightness(1.15);
    }
  }

  animation: pip-sync-glow 2.8s ease-in-out infinite;
`

const ScienceflatSparkle = styled.span`
  position: absolute;
  top: -12px;
  right: -5px;
  font-size: 10px;
  color: var(--Colors-Green-500);
  pointer-events: none;
  filter: drop-shadow(0 0 3px rgba(255, 255, 255, 0.9));

  @keyframes sparkle-pop {
    0%,
    42%,
    92%,
    100% {
      opacity: 0;
      transform: scale(0.3) rotate(-30deg);
    }
    62% {
      opacity: 1;
      transform: scale(1) rotate(15deg);
    }
  }

  animation: sparkle-pop 2.8s ease-in-out infinite;
`

const ScienceflatConsentContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
  ${typo.body02};
  color: var(--Colors-Gray-900);

  .title {
    ${typo.heading05};
    font-weight: 700;
    color: var(--Colors-Gray-900);
  }

  strong {
    color: var(--Colors-Sky-400);
  }
`

const ScienceflatNoPermissionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  text-align: center;

  .body-text {
    ${typo.body02};
    color: var(--Colors-Gray-900);
  }

  .sub-text {
    ${typo.caption01};
    color: var(--Colors-Gray-700);
  }
`
